import {
  LabelCell,
  DescriptionCell,
  ItemDetailLeftPanel,
  ItemDetailRightPanel,
  HalfContainer,
} from "../../../shared/styles";
import {
  CustomButton,
  CustomLabel,
  FlexContainer,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import React, { FC } from "react";
import {
  RelatedInvoicesGridModel,
  SubscriptionDetailModel,
} from "../../../shared/models";
import { InvoiceUtil, SubscriptionUtil } from "../../../shared/utils";
import { RenderPaymentMethod } from "../../../shared/components";
import {
  InvoiceStatus,
  ItemIncreasePriceBy,
  ItemIncreasePriceInterval,
  RecurringFeeInterval,
} from "../../../shared/enums";
import SubscriptionBillingEnds from "./SubscriptionBillingEnds";
import { AccountSettingsStore } from "../../../shared/store";

type IProps = {
  data: SubscriptionDetailModel;
  lastInvoice?: RelatedInvoicesGridModel;
};

const DetailSection: FC<IProps> = ({ data: item, lastInvoice }) => {
  const row = (
    label: string,
    rightColumn: string | React.ReactNode
  ): React.ReactNode => {
    return (
      <FlexContainer className="label-description-row">
        <LabelCell>
          <CustomLabel>{label}</CustomLabel>
        </LabelCell>
        <DescriptionCell>{rightColumn}</DescriptionCell>
      </FlexContainer>
    );
  };

  const colors = SubscriptionUtil.getColorsByStatus(item.status);
  return (
    <>
      <FlexContainer>
        <ItemDetailLeftPanel>
          {row(
            "Status",
            <FlexContainer alignItems="center" spacing={30}>
              <CustomButton
                minWidth={"90px"}
                padding="6px 10px"
                color={colors.color}
                bgColor={colors.bgColor}
                className="status-btn"
              >
                {item.status}
              </CustomButton>
              {item.pastDueDays > 0 && (
                <CustomLabel>{item.pastDueDays} Day(s)</CustomLabel>
              )}
            </FlexContainer>
          )}
          {row(
            "Subscription No",
            <CustomLabel>{item.subscriptionNumber}</CustomLabel>
          )}
          {row(
            "Created",
            <CustomLabel>
              {(item.createdDate &&
                UtilityMethods.formatDateInStringWithTime(item.createdDate)) ||
                "-"}
              <br />
              {/* Created by Name */}
            </CustomLabel>
          )}
          {row(
            "Description",
            <CustomLabel className="description-label">
              {" "}
              {item.description}
            </CustomLabel>
          )}
          {row("Subscription ID", <CustomLabel>{item.id}</CustomLabel>)}
          {row(
            "Setup Fee",
            <CustomLabel fontWeight="600">
              {item.setupFee ? (
                <>
                  {"$" + AccountSettingsStore.formatAmount(item.setupFee, true)}
                  <span className="usd-grey">USD</span>
                </>
              ) : (
                "-"
              )}
            </CustomLabel>
          )}
          {row(
            "Recurring Price",
            <CustomLabel fontWeight="600">
              {item.amount
                ? `$${AccountSettingsStore.formatAmount(item.amount, true)}`
                : "-"}
            </CustomLabel>
          )}
          {row(
            "Recurring Cost",
            <CustomLabel>
              {item.cost ? (
                <>
                  {`$${AccountSettingsStore.formatAmount(item.cost, true)}`}
                  <span className="margin-left-5">
                    {`(${UtilityMethods.calculateMarginPercentage(item.amount, item.cost)}% margin)`}
                  </span>
                </>
              ) : (
                "-"
              )}
            </CustomLabel>
          )}
          {row(
            "Bill Every",
            <CustomLabel>{`${item.billingIntervalCount} ${item.billingInterval}${item.billingIntervalCount > 1 ? "s" : ""}`}</CustomLabel>
          )}
          {row(
            "Billing Starts",
            <CustomLabel>
              <>
                {(item.createdDate &&
                  UtilityMethods.formatDateInStringWithTime(
                    item.createdDate
                  )) ||
                  "-"}
              </>
            </CustomLabel>
          )}
          {row(
            "Billing Ends",
            <CustomLabel>
              <>
                {item.cancelOn ? (
                  UtilityMethods.formatDateInStringWithTime(item.cancelOn)
                ) : (
                  <SubscriptionBillingEnds
                    billingEnds={item.billingEnds}
                    billingEndIntervalCount={item.billingEndIntervalCount}
                    billingEndsAfterInterval={item.billingEndsAfterInterval}
                  />
                )}
              </>
            </CustomLabel>
          )}
        </ItemDetailLeftPanel>
        <ItemDetailRightPanel className="blue-label-container">
          {" "}
          {row(
            "Contact",
            <CustomLabel>{item.contact?.displayValue}</CustomLabel>
          )}{" "}
          {/* {row("Order", <CustomLabel>381</CustomLabel>)}{" "} */}
          {item.lastPayment &&
            row(
              "Last Payment",
              <>
                <FlexContainer fxLayout="column" spacing={10}>
                  <CustomLabel>
                    {UtilityMethods.formatDateInStringWithTime(
                      item.lastPayment.createdDate
                    )}
                  </CustomLabel>
                  <CustomLabel>
                    {" "}
                    $
                    {AccountSettingsStore.formatAmount(
                      item.lastPayment.amount,
                      true
                    )}
                  </CustomLabel>
                </FlexContainer>
                <FlexContainer
                  alignItems="center"
                  spacing={30}
                  padding="10px 0px 0px"
                >
                  <RenderPaymentMethod
                    paymentMethod={item.lastPayment.paymentMethod}
                  />
                </FlexContainer>
              </>
            )}{" "}
          {item.lastInvoice &&
            row(
              "Last Invoice",
              <FlexContainer fxLayout="column" spacing={10}>
                <CustomLabel>
                  {UtilityMethods.formatDateInString(item.lastInvoice.dueDate)}
                </CustomLabel>
                <CustomLabel>
                  {" "}
                  $
                  {AccountSettingsStore.formatAmount(
                    item.lastInvoice.netAmount,
                    true
                  )}
                </CustomLabel>
                <CustomButton
                  bgColor={
                    InvoiceUtil.getColorsByStatus(item.lastInvoice.status)
                      .bgColor
                  }
                  color={
                    InvoiceUtil.getColorsByStatus(item.lastInvoice.status).color
                  }
                >
                  {InvoiceStatus[item.lastInvoice.status]}
                </CustomButton>
              </FlexContainer>
            )}
          {row(
            "Last Invoice",
            <CustomLabel>
              <>
                {lastInvoice ? (
                  <>
                    {UtilityMethods.formatDateInStringWithTime(
                      lastInvoice.createdDate
                    )}
                  </>
                ) : (
                  ""
                )}
              </>
            </CustomLabel>
          )}
          {item.paymentDueDate &&
            row(
              "Next Invoice",
              <>
                {UtilityMethods.formatDateInStringWithTime(item.paymentDueDate)}
                <br />
                {"$" + AccountSettingsStore.formatAmount(item.amount, true)}
              </>
            )}
        </ItemDetailRightPanel>
      </FlexContainer>
      <FlexContainer>
        <HalfContainer>
          <CustomLabel className="header-midnight-blue">
            Additional Fees
          </CustomLabel>
          {row(
            "Price Increase",
            <CustomLabel>
              {item.additionalFees &&
              ItemIncreasePriceInterval[
                item.additionalFees.increasePriceInterval
              ] !== ItemIncreasePriceInterval.None ? (
                <>
                  {ItemIncreasePriceBy[item.additionalFees.increasePriceBy] ===
                  ItemIncreasePriceBy.Amount
                    ? "$" + item.additionalFees.increasePriceAmount
                    : item.additionalFees.increasePriceAmount + "%"}
                  {" " +
                    ItemIncreasePriceInterval[
                      item.additionalFees.increasePriceInterval
                    ]}
                </>
              ) : (
                "-"
              )}
            </CustomLabel>
          )}
          {row(
            "Recurring Fee",
            <CustomLabel>
              {item.additionalFees &&
              RecurringFeeInterval[item.additionalFees.recurringFeeInterval] !==
                RecurringFeeInterval.None ? (
                <>{"$" + item.additionalFees.recurringFee}</>
              ) : (
                "-"
              )}
            </CustomLabel>
          )}
        </HalfContainer>
        <HalfContainer>
          <CustomLabel className="header-midnight-blue">
            Cancellation Terms
          </CustomLabel>
          {row(
            "Required Notice",
            item.cancelationTerms?.daysNotice &&
              item.cancelationTerms?.daysNotice !== ""
              ? `${item.cancelationTerms?.daysNotice} days`
              : "None"
          )}
          {row(
            "Minimum Due",
            item.cancelationTerms?.minimumDue
              ? "$" + item.cancelationTerms?.minimumDue
              : "None"
          )}
          {row(
            "Cancellation Fee",
            item.cancelationTerms?.cancellationFee
              ? "$" + item.cancelationTerms?.cancellationFee
              : "None"
          )}
        </HalfContainer>
      </FlexContainer>
      {/* <CustomLabel fontSize="20px" fontWeight="600" color="#2C3E50" margin="50px 0px 30px">
                Funding
            </CustomLabel> */}
      {/* 
            {row(
                'Next Invoice',
                <CustomLabel>
                    {item.nextInvoiceOn ? (
                        <>
                            {UtilityMethods.formatDateInStringWithTime(item.nextInvoiceOn)}
                            <br />
                            {'$' +AccountSettingsStore.formatAmount(item.amount)}
                        </>
                    ) : (
                        ''
                    )}
                </CustomLabel>,
            )} */}
    </>
  );
};
export default DetailSection;
